<template>
  <app-modal
    ref="modal"
    v-bind="$attrs"
    class="modal-container"
    content-class="modal-medium"
    title="URLフィルタリングプロファイル設定参照"
  >
    <template v-if="isLoaded" v-slot:body="{ cancel }">
      <v-card-text class="pa-0">
        <div class="modal-body">
          <v-container>
            <v-row>
              <v-col class="text-right">
                <v-btn
                  color="primary"
                  v-permission.config
                  @click="showProfileUrlFilteringModify"
                >
                  変更
                </v-btn>
                <v-btn
                  v-permission.config
                  v-if="!urlProfile.isPolicyInUse"
                  color="primary"
                  @click="deleteUrlFiltering"
                  class="ml-2"
                  >削除</v-btn
                >
              </v-col>
            </v-row>

            <!-- 名前 -->
            <v-row>
              <v-col cols="12">
                <app-label-group
                  label="名前"
                  :value="urlProfile.profileUrlName"
                />
              </v-col>
            </v-row>

            <!-- ブラックリスト -->
            <v-row>
              <v-col cols="12">
                <app-label-group label="ブラックリスト">
                  <template #value>
                    <div v-if="urlProfile.blackList.length">
                      <p
                        v-for="(url, index) in urlProfile.blackList"
                        :key="index"
                        class="ml-3 mb-2"
                      >
                        {{ url }}
                      </p>
                    </div>
                    <span v-else class="ml-3">-</span>
                  </template>
                </app-label-group>
              </v-col>
            </v-row>

            <!-- ブラックリストの動作 -->
            <v-row>
              <v-col cols="12">
                <app-label-group
                  label="ブラックリストの動作"
                  :value="
                    $filter.enumTo(urlProfile.blackListAction, 'actionType')
                  "
                />
              </v-col>
            </v-row>

            <!-- ホワイトリスト -->
            <v-row>
              <v-col cols="12">
                <app-label-group label="ホワイトリスト">
                  <template #value>
                    <div v-if="urlProfile.whiteList.length">
                      <p
                        v-for="(url, index) in urlProfile.whiteList"
                        :key="index"
                        class="ml-3 mb-2"
                      >
                        {{ url }}
                      </p>
                    </div>
                    <span v-else class="ml-3">-</span>
                  </template>
                </app-label-group>
              </v-col>
            </v-row>

            <!-- カテゴリ -->

            <v-row>
              <v-col cols="12">
                <app-label-group label="カテゴリ" />
              </v-col>
            </v-row>
            <div>
              <v-table>
                <thead class="v-table-head">
                  <tr>
                    <th class="text-center">カテゴリ名</th>
                    <th class="text-center th-action">アクション</th>
                  </tr>
                </thead>
                <tbody>
                  <template
                    v-for="(category, idx) in urlProfile.categoryList"
                    :key="idx"
                  >
                    <tr>
                      <td>{{ category.categoryName }}</td>
                      <td class="text-center">
                        <v-chip
                          variant="flat"
                          :color="
                            $filter.enumTo(
                              category.actionType,
                              'actionTypeVariant'
                            )
                          "
                          label
                          density="compact"
                          class="font-weight-bold"
                        >
                          {{
                            $filter.enumTo(category.actionType, "actionType")
                          }}
                        </v-chip>
                      </td>
                    </tr>
                  </template>
                </tbody>
              </v-table>
            </div>

            <!-- 備考 -->
            <v-row>
              <v-col cols="12">
                <app-label-group
                  label="備考"
                  :value="$filter.emptyTo(urlProfile.description, '-')"
                  class="text-pre-wrap"
                />
              </v-col>
            </v-row>
          </v-container>
        </div>
      </v-card-text>
      <v-divider></v-divider>

      <v-card-actions>
        <v-list-item class="w-100 pa-1">
          <template v-slot:append>
            <v-btn @click="cancel" variant="outlined" color="primary"
              >戻る</v-btn
            >
          </template>
        </v-list-item>
      </v-card-actions>
    </template>
  </app-modal>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import type { PropType } from "vue";
import { VueFinalModal } from "vue-final-modal";
import ProfileUrlFilteringEdit from "@/modals/applianceObject/ProfileUrlFilteringEdit.vue";
import type { DeleteUrlProfile, UrlProfile } from "@/apis/UrlProfileApi";

export default defineComponent({
  name: "ProfileUrlFilteringReference",
  props: {
    profileUrlSeq: {
      type: String as PropType<string>,
      required: true,
    },
    type4Id: {
      type: String as PropType<string>,
      required: false,
      default: null,
    },
  },
  data() {
    return {
      isLoaded: false,
      urlProfile: {} as UrlProfile,
    };
  },
  async mounted() {
    await this.load();
    this.isLoaded = true;
  },
  methods: {
    /** プロファイル(URLフィルタリング)詳細を取得 */
    async load() {
      if (!this.type4Id) {
        this.urlProfile = await this.$api.urlProfileApi.getType1UrlFiltering(
          this.profileUrlSeq
        );
      } else {
        this.urlProfile = await this.$api.urlProfileApi.getType4UrlFiltering(
          this.type4Id,
          this.profileUrlSeq
        );
      }
    },
    /** URLフィルタリングを編集 */
    async showProfileUrlFilteringModify() {
      await this.$modal.show(ProfileUrlFilteringEdit, {
        urlProfile: this.urlProfile,
        profileUrlSeq: this.profileUrlSeq,
        type4Id: this.type4Id,
        profileUrlKeyId: this.urlProfile.profileUrlKeyId,
      });
      (this.$refs.modal as typeof VueFinalModal & { ok: () => void }).ok();
    },
    /** URLフィルタリングを削除 */
    async deleteUrlFiltering() {
      // 確認ダイアログの表示
      await this.$confirm("プロファイルを削除します。よろしいですか？");

      // リクエストデータの作成
      const reqData: DeleteUrlProfile = {
        profileUrlSeqList: [this.profileUrlSeq],
        profileUrlKeyId: this.urlProfile.profileUrlKeyId,
        note: null,
      };

      // プロファイル(URLフィルタリング)削除
      if (!this.type4Id) {
        await this.$api.urlProfileApi.deleteType1UrlFiltering(reqData);
      } else {
        await this.$api.urlProfileApi.deleteType4UrlFiltering(
          this.type4Id,
          reqData
        );
      }
      (this.$refs.modal as typeof VueFinalModal & { ok: () => void }).ok();
    },
  },
});
</script>

<style lang="scss" scoped>
.th-action {
  width: 270px;
}
</style>
